/**
 * Single source of truth for DISPLAY percent formatting (audit UX-06).
 *
 * One agreed style: the "%" sits flush against the number — NO space
 * ("15%", "0.4%", "+15%", "-12%"). Components must not hand-roll
 * `` `${x}%` `` / `x.toFixed(n) + "%"` with their own spacing.
 *
 * Decimals default to "smart": whole-ish values render with no decimals
 * ("15%"), sub-1% values keep one decimal so they never collapse to a
 * misleading "0%" ("0.4%"). Pass `decimals` to force a fixed count.
 */
export interface FormatPercentOptions {
  /** Fixed decimal places. Omit for smart (|value| >= 1 → 0, else 1). */
  decimals?: number;
  /** Prepend "+" for positive values (e.g. variance "+15%"). */
  sign?: boolean;
  /** Render negatives as "(12%)" instead of "-12%" (accounting style). */
  parensNegative?: boolean;
}

/**
 * Format a number as a percent string with the "%" flush (no space).
 * Returns "" for non-finite input (null/undefined/NaN), so callers can
 * render a clean blank — pass `value ?? 0` if you want "0%" instead.
 */
export function formatPercent(
  value: number | null | undefined,
  options: FormatPercentOptions = {},
): string {
  const { decimals, sign = false, parensNegative = false } = options;

  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "";

  const places = decimals ?? (n !== 0 && Math.abs(n) < 1 ? 1 : 0);
  const absBody = Math.abs(n).toFixed(places);
  const body = `${absBody}%`;

  // When the value rounds to zero at this precision, render it sign-less
  // ("0%") — never an ugly "-0%" / "+0%" (e.g. -0.4 with decimals: 0).
  if (Number(absBody) === 0) return body;
  if (n < 0) return parensNegative ? `(${body})` : `-${body}`;
  if (sign && n > 0) return `+${body}`;
  return body;
}
